import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiPlus, FiCreditCard, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import StatusPill from '../../components/ui/StatusPill';
import ErrorBanner from '../../components/ui/ErrorBanner';
import EmptyState from '../../components/ui/EmptyState';
import { Select } from '../../components/ui/Input';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';

import {
  listReimbursementsPaginated,
  listMyReimbursementsPaginated,
  filterReimbursements
} from '../../api/reimbursements';
import { WorkflowStatus, enumOptions } from '../../enums';
import { useAuth } from '../../auth/AuthContext';

import ReimbursementForm from './ReimbursementForm';
import ReimbursementDetailDrawer from './ReimbursementDetailDrawer';

const STATUS_FILTERS = [
  { value: 'ALL', label: 'All statuses' },
  ...enumOptions(WorkflowStatus),
];

const DEPT_OPTIONS = [
  { value: '', label: 'All departments' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'HR', label: 'HR' },
  { value: 'IT', label: 'IT' },
  { value: 'PAYROLL', label: 'Payroll' },
  { value: 'PROCUREMENT', label: 'Procurement' },
];

export default function ReimbursementsListPage() {
  const { isAdmin } = useAuth();

  const [scope, setScope] = useState(isAdmin ? 'ALL' : 'MINE');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [deptFilter, setDeptFilter] = useState('');
  
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchReimbursements = async ({ queryKey }) => {
    const [_key, qScope, qStatus, qDept, qPage, qSize] = queryKey;
    const params = { page: qPage, size: qSize };
    if (qStatus !== 'ALL') params.status = qStatus;
    if (qDept && qScope === 'ALL') params.department = qDept;
    
    if (qStatus !== 'ALL' || (qDept && qScope === 'ALL')) {
       // Workaround for backend JDBC issue with null dates in Postgres
       params.startDate = '2000-01-01';
       params.endDate = '2099-12-31';
       return filterReimbursements(params);
    }
    return qScope === 'ALL' ? listReimbursementsPaginated(params) : listMyReimbursementsPaginated(params);
  };

  const { data: pageData, isLoading, error, refetch } = useQuery({
    queryKey: ['reimbursements', scope, statusFilter, deptFilter, page, pageSize],
    queryFn: fetchReimbursements,
  });

  const content = pageData?.content || pageData || [];
  const totalPages = pageData?.totalPages || 0;

  const columns = [
    {
      key: 'title',
      header: 'Title',
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-slate-800">
            {r.title || `Claim #${r.id}`}
          </div>
          <div className="truncate text-xs text-slate-500">
             {r.category ? String(r.category).replace(/_/g, ' ') : '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'employeeUsername',
      header: 'Employee',
      render: (r) => <span className="text-slate-600">{r.employeeUsername || r.createdByUsername}</span>,
    },
    {
      key: 'department',
      header: 'Department',
      render: (r) => <span className="text-slate-600">{r.department || '—'}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (r) => (
        <span className="font-medium tabular-nums">
          <CurrencyDisplay value={r.amount} />
        </span>
      ),
    },
    {
      key: 'workflowStatus',
      header: 'Status',
      render: (r) => <StatusPill value={r.workflowStatus} />,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (r) => <DateDisplay value={r.createdAt} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (r) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setSelected(r);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <AppLayout title="Reimbursements" breadcrumb="Finance / Reimbursements">
      <PageHeader
        title={scope === 'MINE' ? 'My Claims' : 'All Claims'}
        description={
          isAdmin
            ? 'Review and approve employee reimbursement claims.'
            : 'File and track your reimbursement claims.'
        }
        actions={
          <Button icon={FiPlus} onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white border-0 shadow-sm">
            New Claim
          </Button>
        }
      />

      {error && (
        <div className="mb-6">
          <ErrorBanner
            error={error}
            title="Could not load reimbursements"
            onRetry={refetch}
          />
        </div>
      )}

      {isAdmin && (
        <div className="mb-6 flex gap-1 border-b border-slate-200 pb-px">
          <button
            type="button"
            onClick={() => { setScope('ALL'); setPage(0); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              scope === 'ALL'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            All Claims
          </button>
          <button
            type="button"
            onClick={() => { setScope('MINE'); setPage(0); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              scope === 'MINE'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            My Claims
          </button>
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-4 bg-white p-4 rounded-lg shadow-sm border border-slate-200">
        <Select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="sm:w-48 bg-slate-50"
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
        
        {scope === 'ALL' && (
          <Select
            value={deptFilter}
            onChange={(e) => { setDeptFilter(e.target.value); setPage(0); }}
            className="sm:w-48 bg-slate-50"
          >
            {DEPT_OPTIONS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </Select>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <DataTable
          columns={columns}
          rows={content}
          loading={isLoading}
          error={!!error}
          empty={
            <EmptyState
              icon={FiCreditCard}
              title={
                statusFilter !== 'ALL'
                  ? `No ${String(statusFilter).toLowerCase().replace(/_/g, ' ')} claims`
                  : scope === 'MINE'
                  ? 'You have not filed any claims yet'
                  : 'No reimbursements yet'
              }
              description={
                scope === 'MINE'
                  ? 'Create a new claim to request reimbursement for a business expense.'
                  : 'Try a different filter.'
              }
              action={
                statusFilter === 'ALL' && (
                  <Button icon={FiPlus} onClick={() => setCreateOpen(true)} variant="outline">
                    New Claim
                  </Button>
                )
              }
            />
          }
          onRowClick={(row) => setSelected(row)}
        />
        
        {/* Pagination Controls */}
        {(content.length > 0 || page > 0) && (
          <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
            <div className="flex items-center gap-3 mb-3 sm:mb-0">
              <span className="text-sm text-slate-500">
                Page Size:
              </span>
              <select 
                value={pageSize} 
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
                className="w-20 px-2 py-1 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 mr-2">
                Page <span className="font-medium text-slate-700">{page + 1}</span> of <span className="font-medium text-slate-700">{totalPages}</span>
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                icon={FiChevronLeft} 
                disabled={page === 0} 
                onClick={() => setPage(p => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page >= totalPages - 1} 
                onClick={() => setPage(p => p + 1)}
              >
                Next <FiChevronRight className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <ReimbursementForm
        open={createOpen}
        onClose={() => { setCreateOpen(false); refetch(); }}
        onCreated={(data) => {
          setSelected(data);
          refetch();
        }}
      />

      <ReimbursementDetailDrawer
        open={!!selected}
        reimbursementId={selected?.id}
        fallback={selected}
        onClose={() => setSelected(null)}
      />
    </AppLayout>
  );
}
